import { pacificToday } from '../../../shared/dates';
import type { SignalSource } from '../../../shared/enums';
import {
  CITY_LIST_URLS,
  FBN_LOOKBACK_DAYS,
  FBN_SEARCH_URL,
  HDL_BROWSER_HOSTS,
  INTAKE_ADDRESS,
  OCHCA_PLAN_REVIEW_URL,
} from '../../../shared/fields';
import type { SignalList, SourceStatus } from '../../../shared/types';
import { diffDays, formatDate, formatDateTime, timeAgo, timeAgoShort, toIsoDate } from './dates';
import { BOOKMARKED, MONTHLY, SOURCE_NAMES, SOURCE_ORDER, UPLOADED, monthName } from './signals';

/** The health of every source, for the trust line and the sources sheet (D72, D74): what reads itself and
 *  when, what the rep still has to bring in and how overdue it is, what is failing. Pure. */

/** How long a manual source may go without an import before it needs the rep. */
export const NEED_YOU_DAYS = { ochca: 7, fbn: FBN_LOOKBACK_DAYS, bookmark: 30, upload: 35 } as const;
/** A publisher's own list older than this is said to be stale (Yorba Linda's July list in October). */
export const STALE_LIST_DAYS = 60;

export type SourceMode = 'server' | 'bookmark' | 'upload';
export type SourceState = 'ok' | 'failing' | 'red' | 'blocked' | 'stale' | 'waiting' | 'nothing' | 'off';

export interface SourceRow {
  source: SignalSource | 'places_retention' | 'roster_geocode';
  name: string;
  mode: SourceMode;
  state: SourceState;
  /** "Every day at 8 AM · last OK 2h ago", "Last import Oct 13, 11:45 AM · 4d ago", "Never imported". */
  text: string;
  /** What the rep does for a manual source. */
  hint: string | null;
  action: 'open' | 'search' | 'import' | null;
  url: string | null;
}

export interface SourceRows {
  needsYou: SourceRow[];
  current: SourceRow[];
  nothingYet: SourceRow[];
}

type Health = Pick<SignalList, 'checked' | 'updated' | 'errors' | 'produced' | 'sources'>;

const modeOf = (s: SignalSource): SourceMode =>
  s === 'ochca' || s === 'fbn' || BOOKMARKED.has(s) ? 'bookmark' : UPLOADED.has(s) ? 'upload' : 'server';
const dueDays = (s: SignalSource): number =>
  s === 'ochca'
    ? NEED_YOU_DAYS.ochca
    : s === 'fbn'
      ? NEED_YOU_DAYS.fbn
      : UPLOADED.has(s)
        ? NEED_YOU_DAYS.upload
        : NEED_YOU_DAYS.bookmark;

const hosts = HDL_BROWSER_HOSTS as Partial<Record<SignalSource, string>>;
const listPages = CITY_LIST_URLS as Partial<Record<SignalSource, string>>;

const linkOf = (s: SignalSource): Pick<SourceRow, 'action' | 'url' | 'hint'> => {
  const name = SOURCE_NAMES[s];
  if (s === 'ochca')
    return {
      action: 'open',
      url: OCHCA_PLAN_REVIEW_URL,
      hint: 'On your computer: open the plan-review page and click Send to CRM in the bookmarks bar.',
    };
  if (s === 'fbn')
    return {
      action: 'search',
      url: FBN_SEARCH_URL,
      hint: `Open the county's filings search and click Send to CRM (brings ${FBN_LOOKBACK_DAYS} days).`,
    };
  if (BOOKMARKED.has(s))
    return {
      action: 'search',
      url: `${hosts[s] ?? ''}/Search/Index/BusinessLicense`,
      hint: `Open ${name}'s license search and click Send to CRM (brings 45 days).`,
    };
  if (UPLOADED.has(s))
    return {
      action: 'import',
      url: listPages[s] ?? null,
      hint: `Download this month's new-business list and import it, or email it to ${INTAKE_ADDRESS} from your own address with the city's name in the subject.`,
    };
  return { action: null, url: null, hint: null };
};

const listNote = (s: SignalSource, day: string | undefined, now: Date): string | null =>
  day
    ? MONTHLY.has(s)
      ? `latest list ${monthName(day)}`
      : `list from ${formatDate(day, pacificToday(now))}`
    : null;

const manualRow = (s: SignalSource, list: Health, now: Date): SourceRow => {
  const at = list.checked[s];
  const base = { source: s, name: SOURCE_NAMES[s], mode: modeOf(s), ...linkOf(s) };
  // a list mailed in that the reader refused: the table keeps its error (D78), said until a later
  // import — by hand or by mail — lands
  const mail = (list.sources ?? []).find((x) => x.name === s);
  const refused =
    mail?.last_error && (!at || (mail.last_attempt_at ?? '') > at)
      ? `Last email could not be read: ${mail.last_error}`
      : null;
  if (!at)
    return { ...base, state: 'waiting', text: ['Never imported', refused].filter(Boolean).join(' · ') };
  const days = diffDays(pacificToday(now), pacificToday(new Date(at)));
  const note = listNote(s, list.updated[s], now);
  return {
    ...base,
    state: days > dueDays(s) ? 'stale' : 'ok',
    text: [`Last import ${formatDateTime(at, toIsoDate(now))} · ${timeAgo(at, now)}`, note, refused]
      .filter(Boolean)
      .join(' · '),
  };
};

const serverRow = (s: SignalSource, list: Health, now: Date): SourceRow => {
  const at = list.checked[s];
  const error = list.errors?.[s] ?? null;
  const day = list.updated[s];
  const stale = day ? diffDays(pacificToday(now), day) > STALE_LIST_DAYS : false;
  const produced = (list.produced[s] ?? 0) > 0;
  const state: SourceState = error ? 'failing' : stale ? 'stale' : !produced ? 'nothing' : 'ok';
  const text = [
    'Every day at 8:30 AM',
    at ? `last OK ${timeAgoShort(at, now)}` : 'not yet',
    error ? 'retried 8 PM' : null,
    listNote(s, day, now),
    error,
  ]
    .filter(Boolean)
    .join(' · ');
  return {
    source: s,
    name: SOURCE_NAMES[s],
    mode: 'server',
    state,
    text,
    hint: null,
    action: null,
    url: null,
  };
};

/** A server row as the sources table tells it (D77): its cadence and last success, its failures in a row
 *  and the last error, a bot check that sends the rep to the bookmark on the site's own search, or Off. */
const tableRow = (s: SignalSource, status: SourceStatus, list: Health, now: Date): SourceRow => {
  const base = {
    source: s,
    name: SOURCE_NAMES[s],
    mode: 'server' as const,
    hint: null,
    action: null,
    url: null,
  };
  if (status.state === 'off') return { ...base, state: 'off', text: 'Off' };
  if (status.state === 'blocked')
    return {
      ...base,
      state: 'blocked',
      text: status.cadence,
      hint: 'Answers a bot check: open its license search and click Send to CRM there.',
      action: 'search',
      url: listPages[s] ?? null,
    };
  const produced = (list.produced[s] ?? 0) > 0;
  const state: SourceState =
    status.state === 'red' || status.state === 'failing'
      ? status.state
      : !produced
        ? 'nothing'
        : status.state;
  const text = [
    status.cadence,
    status.last_ok_at ? `last OK ${timeAgoShort(status.last_ok_at, now)}` : 'not yet',
    status.consecutive_failures
      ? `${status.consecutive_failures} failure${status.consecutive_failures === 1 ? '' : 's'} in a row`
      : null,
    listNote(s, list.updated[s], now),
    status.last_error,
  ]
    .filter(Boolean)
    .join(' · ');
  return { ...base, state, text };
};

/** Every source in the inbox's order, grouped by what it asks of the rep. A failing source is always in
 *  sight; a source that never brought a business and is not failing is "nothing yet"; a blocked one needs
 *  the rep's bookmark. A list from before the sources table (no `sources`) reads as it did. */
export const sourceRows = (list: Health, now: Date): SourceRows => {
  const rows: SourceRows = { needsYou: [], current: [], nothingYet: [] };
  const table = new Map((list.sources ?? []).map((s) => [s.name, s]));
  for (const s of SOURCE_ORDER) {
    if (modeOf(s) === 'server') {
      const status = table.get(s);
      const row = status ? tableRow(s, status, list, now) : serverRow(s, list, now);
      (row.state === 'blocked'
        ? rows.needsYou
        : row.state === 'nothing' || row.state === 'off'
          ? rows.nothingYet
          : rows.current
      ).push(row);
    } else {
      const row = manualRow(s, list, now);
      (row.state === 'waiting' || row.state === 'stale' ? rows.needsYou : rows.current).push(row);
    }
  }
  const retention = table.get('places_retention');
  if (retention) rows.current.push({
    source: 'places_retention', name: 'Google cache retention', mode: 'server', state: retention.state,
    text: [retention.cadence, retention.last_ok_at ? `Last OK ${timeAgoShort(retention.last_ok_at, now)}` : 'Not run yet', retention.last_error].filter(Boolean).join(' · '),
    hint: 'Deletes expired Google working data; never performs lookups.', action: null, url: null,
  });
  const roster = table.get('roster_geocode');
  if (roster) rows.current.push({
    source: 'roster_geocode', name: 'Public roster catch-up', mode: 'server', state: roster.state,
    text: [roster.last_ok_at ? `Last OK ${timeAgoShort(roster.last_ok_at, now)}` : 'Not run yet',
      roster.consecutive_failures ? `${roster.consecutive_failures} failure${roster.consecutive_failures === 1 ? '' : 's'} in a row` : null,
      roster.last_error,
      roster.next_due_at ? `Next automatic attempt ${formatDateTime(roster.next_due_at, toIsoDate(now))}` : 'Automatic attempt when due',
    ].filter(Boolean).join(' · '),
    hint: 'Retries automatically while public addresses remain.', action: null, url: null,
  });
  return rows;
};

/** "Checked 2h ago · 14 sources · 7 need you · 1 failing": the newest server read, the sources that have
 *  brought a business, and what needs the rep or is failing (the zero parts left out). */
export const trustLine = (list: Health, now: Date): { text: string; needsYou: number; failing: number } => {
  const rows = sourceRows(list, now);
  const latest = SOURCE_ORDER.filter((s) => modeOf(s) === 'server')
    .map((s) => (list.sources ?? []).find(row => row.name === s)?.last_ok_at ?? list.checked[s])
    .filter((at): at is string => !!at)
    .sort()
    .at(-1);
  const sources = Object.values(list.produced).filter((n) => (n ?? 0) > 0).length;
  const needsYou = rows.needsYou.length;
  const failing = [...rows.current, ...rows.nothingYet].filter(
    (r) => r.state === 'failing' || r.state === 'red',
  ).length;
  const parts = [
    latest ? `Checked ${timeAgoShort(latest, now)}` : 'No successful automated read yet',
    `${sources} sources with businesses`,
    needsYou ? `${needsYou} source tasks need you` : null,
    failing ? `${failing} failing` : null,
  ];
  return { text: parts.filter(Boolean).join(' · '), needsYou, failing };
};
